import { useEffect, useState } from "react";
import client from "../api/client";
import type {
	TimeOffData,
	TimeOffHistoryEntry,
	TimeOffMatrixEntry,
} from "../api/QuikTripClient";
import { fmtDate } from "../lib/util";

const STATUS_BADGE: Record<number, string> = {
	1: "badge-accent",
	2: "badge-error",
	3: "badge-success",
	4: "badge-neutral",
};

function BalanceCard({ entry }: { entry: TimeOffMatrixEntry }) {
	const pct =
		entry.beginningBalance > 0
			? Math.round((entry.available / entry.beginningBalance) * 100)
			: 0;
	return (
		<div className="bg-base-200 rounded-xl p-4 flex flex-col gap-2">
			<div className="text-xs text-base-content/50 uppercase tracking-widest truncate">
				{entry.reason}
			</div>
			<div className="flex items-end justify-between">
				<span className="text-2xl font-black text-base-content">
					{entry.available}
				</span>
				<span className="text-xs text-base-content/40">
					of {entry.beginningBalance}
				</span>
			</div>
			<div className="w-full h-1.5 rounded-full bg-base-300 overflow-hidden">
				<div
					className="h-full rounded-full bg-primary transition-all"
					style={{ width: `${pct}%` }}
				/>
			</div>
			<div className="text-xs text-base-content/40">{entry.taken} used</div>
		</div>
	);
}

function HistoryRow({ entry }: { entry: TimeOffHistoryEntry }) {
	return (
		<div className="flex items-center justify-between px-4 py-3">
			<div>
				<div className="text-sm font-medium">{entry.explanationCodeDesc}</div>
				<div className="text-xs text-base-content/50 mt-0.5">
					{fmtDate(entry.startTime)}
				</div>
			</div>
			<span
				className={`badge badge-sm ${STATUS_BADGE[entry.statusId] ?? "badge-neutral"}`}
			>
				{entry.statusDesc}
			</span>
		</div>
	);
}

export function TimeOffPage() {
	const [data, setData] = useState<TimeOffData | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		client
			.getAllTimeOff()
			.then(setData)
			.catch((e) => setError(e instanceof Error ? e.message : "Failed to load"))
			.finally(() => setLoading(false));
	}, []);

	if (loading) {
		return (
			<div className="flex items-center justify-center h-full">
				<span className="loading loading-spinner loading-md text-primary" />
			</div>
		);
	}

	if (error || !data) {
		return (
			<div className="p-6 text-center text-error text-sm">
				{error ?? "No data"}
			</div>
		);
	}

	const topLevel = data.matrix.filter(
		(e) => e.isGroupParent || e.matrixGroupId === null,
	);

	return (
		<div className="flex flex-col gap-5 p-4">
			{/* Balances */}
			<div>
				<div className="text-xs font-semibold text-base-content/50 uppercase tracking-widest mb-2 px-1">
					Balances
				</div>
				<div className="grid grid-cols-2 gap-3">
					{topLevel.map((e) => (
						<BalanceCard key={e.explanationCodeId} entry={e} />
					))}
				</div>
			</div>

			{/* Upcoming */}
			{data.futureTimeOff.length > 0 && (
				<div>
					<div className="text-xs font-semibold text-base-content/50 uppercase tracking-widest mb-2 px-1">
						Upcoming
					</div>
					<div className="rounded-xl bg-base-200 overflow-hidden divide-y divide-base-300">
						{data.futureTimeOff.map((e) => (
							<HistoryRow
								key={`${e.startTime}-${e.explanationCodeId}`}
								entry={e}
							/>
						))}
					</div>
				</div>
			)}

			{/* History */}
			{data.historyTimeOff.length > 0 && (
				<div>
					<div className="text-xs font-semibold text-base-content/50 uppercase tracking-widest mb-2 px-1">
						History
					</div>
					<div className="rounded-xl bg-base-200 overflow-hidden divide-y divide-base-300">
						{data.historyTimeOff.map((e) => (
							<HistoryRow
								key={`${e.startTime}-${e.explanationCodeId}`}
								entry={e}
							/>
						))}
					</div>
				</div>
			)}
		</div>
	);
}
